/**
 * The vote affordance — the primary control on this page.
 *
 * It is deliberately the leftmost and tallest element of a row: voting is what
 * turns a pile of complaints into a priority list, and the layout should say so.
 * State is carried by fill *and* by the accessible label, never by colour alone.
 */

import { ChevronUpIcon } from "@/lib/icons";
import { cn } from "@/lib/utils";

interface VoteButtonProps {
  voteCount: number;
  hasVoted: boolean;
  onToggle: () => void;
  size?: "sm" | "lg";
  disabled?: boolean;
  /**
   * Rendered on the dialog's brand-coloured header. The default palette is
   * primary-on-light, which would be near-invisible against bg-primary, so this
   * switches to white-on-translucent.
   */
  onPrimary?: boolean;
}

export function VoteButton({
  voteCount,
  hasVoted,
  onToggle,
  size = "sm",
  disabled = false,
  onPrimary = false,
}: VoteButtonProps) {
  const isLarge = size === "lg";

  return (
    <button
      type="button"
      onClick={(event) => {
        // Rows are clickable; voting must not also open the report.
        event.stopPropagation();
        onToggle();
      }}
      disabled={disabled}
      aria-pressed={hasVoted}
      aria-label={hasVoted ? "Peru ääni" : "Äänestä ilmoitusta"}
      className={cn(
        "flex flex-col items-center justify-center gap-0.5 transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        isLarge && !onPrimary &&
          "w-14 rounded-lg border border-primary bg-primary/10 py-1.5 text-primary",
        isLarge && onPrimary &&
          "w-14 shrink-0 rounded-lg border border-white/30 bg-white/15 py-1.5 text-primary-foreground hover:bg-white/25",
        !isLarge &&
          "h-full w-14 border-r bg-muted/40 text-muted-foreground hover:bg-primary/10 hover:text-primary",
        !isLarge && hasVoted && "bg-primary/10 text-primary",
      )}
    >
      <ChevronUpIcon
        className={cn(isLarge ? "h-5 w-5" : "h-4 w-4", hasVoted && "fill-current")}
      />
      <span
        className={cn(
          "font-mono font-bold tabular-nums",
          isLarge ? "text-lg" : "text-sm",
          isLarge && !onPrimary && "text-primary",
          !isLarge && hasVoted && "text-primary",
        )}
      >
        {voteCount}
      </span>
      {/* No "ääntä" caption: the chevron plus the number already reads as a vote
          count — the list rows prove it — and the word cost a third row of
          height in the dialog header. The accessible label still spells it out. */}
    </button>
  );
}
